import { useEffect, useState, lazy, Suspense } from 'react';
import { HashRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { AnimatePresence, MotionConfig, motion } from 'framer-motion';
import Home from './pages/Home';
import AppNavbar from './components/Navbar';
import { ExhibitionProvider } from './context/ExhibitionContext';
import { LanguageProvider } from './context/LanguageContext';
import BottomNavBar from './components/BottomNavBar';
import ErrorBoundary from './components/boundaries/ErrorBoundary';
import RouteErrorBoundary from './components/boundaries/RouteErrorBoundary';
import IOSInstallModal from './components/IOSInstallModal';
import AndroidInstallModal from './components/AndroidInstallModal';
import AppToasts from './components/AppToasts';
import { usePWAKeyboardHandler } from './hooks/usePWAKeyboardHandler';
import { userIdentificationService } from './utils/userIdentificationService';
import { externalNotificationService } from './utils/externalNotificationService';
import { NotificationTester } from './components/Notifications';
import DeviceGuard from './components/DeviceGuard';

// Route-level code splitting — Home stays eager for instant first paint,
// everything else loads on demand (QR scanner alone pulls in two QR libs).
const Exhibitors = lazy(() => import('./pages/Exhibitors'));
const Exhibits = lazy(() => import('./pages/Exhibits'));
const Pavilions = lazy(() => import('./pages/Pavilions'));
const QRScanner = lazy(() => import('./pages/QRScanner'));
const Tickets = lazy(() => import('./pages/Tickets'));
const Services = lazy(() => import('./pages/Services'));
const ExhibitorDetail = lazy(() => import('./pages/ExhibitorDetail'));
const ExhibititAssociatedExhibitors = lazy(() => import('./pages/ExhibitAssosiatedExhibitors'));
const PavilionExhibitList = lazy(() => import('./pages/PavilionExhibitList'));
const MapScreen = lazy(() => import('./pages/MapScreen'));
const Login = lazy(() => import('./pages/Login'));
const UserTypeSelection = lazy(() => import('./components/UserTypeSelection'));
const VisitorProfile = lazy(() => import('./components/VisitorProfile'));
const VisitorRegistration = lazy(() => import('./pages/Register'));
const Info = lazy(() => import('./pages/Info'));
const Events = lazy(() => import('./pages/Events'));
const Favorites = lazy(() => import('./pages/Favorites'));

const SponsorsScreen = lazy(() => import('./pages/Sponsors'));

function AppContent() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // Start with loading true
  const [userIdentification, setUserIdentification] = useState(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '';

  usePWAKeyboardHandler();

  const shouldShowBottomNavbar = [
    '/exhibitors', 
    '/exhibits', 
    '/map', 
    '/pavilions',
    '/pavilion-exhibit-list',
    '/exhibit-associated-exhibitors',
    '/info',
    '/events',
    '/tickets',
    '/services',
    '/sponsors',
    '/favorites'
  ].some(path => location.pathname.startsWith(path));

  // Initialize user tracking on app start
  useEffect(() => {
    const initializeApp = async () => {
      try {
        
        // Initialize user tracking
        const identification = await userIdentificationService.initializeUserTracking({
          appStartTime: new Date().toISOString(),
          initialRoute: location.pathname,
          isStandalone: window.matchMedia('(display-mode: standalone)').matches
        });
        
        setUserIdentification(identification);

         await externalNotificationService.initialize({
         websocketUrl: process.env.REACT_APP_WEBSOCKET_URL || 'wss://api.helexpo.gr/notifications'
       });
      
      // console.log('✅ External notifications initialized');
        
        // Log user stats for debugging
        const userStats = userIdentificationService.getUserStats();

        if (userStats.isReturningUser) {
        } else {
          //console.log('🎉 Welcome new user!');
        }
        
      } catch (error) {
        console.error('❌ Failed to initialize user tracking:', error);
      } finally {
        setIsLoading(false);
      }
    };

    initializeApp();
  }, []); // Empty dependency array - run only once

  // Track route changes
  useEffect(() => {
    if (userIdentification) {
      // Optional: Send route change events to backend
      const routeData = {
        route: location.pathname,
        timestamp: new Date().toISOString(),
        sessionId: userIdentification.sessionId,
        persistentUserId: userIdentification.persistentUserId
      };
      
    }
  }, [location.pathname, userIdentification]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setDeferredPrompt(event);
    };

    const handleAppInstalled = () => {
      // The saved prompt is spent once the app is installed
      setDeferredPrompt(null);
      if (userIdentification) {
        userIdentificationService.sendIdentificationToBackend({
          event: 'pwa_installed',
          timestamp: new Date().toISOString(),
          sessionId: userIdentification.sessionId
        });
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, [userIdentification]);

  // Called by AndroidInstallModal after the native install dialog resolves
  const handleInstallPromptResult = (outcome) => {
    if (userIdentification) {
      userIdentificationService.sendIdentificationToBackend({
        event: outcome === 'accepted' ? 'install_prompt_accepted' : 'install_prompt_dismissed',
        timestamp: new Date().toISOString(),
        sessionId: userIdentification.sessionId
      });
    }
    setDeferredPrompt(null);
  };

  // Show loading screen while initializing
  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p className="loading-text">Loading Helexpo...</p>
      </div>
    );
  }

  return (
    <>
      <style jsx>{`
        .loading-spinner {
          width: 40px;
          height: 40px;
          border: 3px solid rgba(255, 255, 255, 0.1);
          border-radius: 50%;
          border-top-color: var(--accent-color, #0ea5e9);
          animation: spin 1s ease-in-out infinite;
          margin-bottom: 16px;
        }
        
        .loading-text {
          font-size: 1.1rem;
          font-weight: 500;
          opacity: 0.8;
          margin: 0;
        }
        
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
      
      <div className="app-content" data-user-id={userIdentification?.persistentUserId}>
        <AppNavbar />
        <MotionConfig reducedMotion="user">
        <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -10 }}
          transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
        >
        <Suspense
          fallback={
            <div className="route-chunk-loading">
              <div className="loading-spinner"></div>
            </div>
          }
        >
        <Routes location={location}>
          <Route path="/" element={
            <RouteErrorBoundary routeName="Home">
              <Home />
            </RouteErrorBoundary>
          } />
          
          <Route path="/exhibitors" element={
            <RouteErrorBoundary routeName="Exhibitors">
              <Exhibitors />
            </RouteErrorBoundary>
          } />
          
          <Route path="/exhibitors/:id" element={
            <RouteErrorBoundary routeName="Exhibitor Details">
              <ExhibitorDetail />
            </RouteErrorBoundary>
          } />
          
          <Route path="/user-type-selection" element={
            <RouteErrorBoundary routeName="User Selection">
              <UserTypeSelection />
            </RouteErrorBoundary>
          } />
          
          <Route path="/exhibits" element={
            <RouteErrorBoundary routeName="Exhibits">
              <Exhibits />
            </RouteErrorBoundary>
          } />
          
          <Route path="/exhibit-associated-exhibitors/:id" element={
            <RouteErrorBoundary routeName="Exhibit Details">
              <ExhibititAssociatedExhibitors />
            </RouteErrorBoundary>
          } />
          
          <Route path="/pavilions" element={
            <RouteErrorBoundary routeName="Pavilions">
              <Pavilions />
            </RouteErrorBoundary>
          } />
          
          <Route path="/pavilion-exhibit-list/:pavilionId" element={
            <RouteErrorBoundary routeName="Pavilion Details">
              <PavilionExhibitList />
            </RouteErrorBoundary>
          } />
          
          <Route path="/qr-scanner" element={
            <RouteErrorBoundary routeName="QR Scanner">
              <QRScanner />
            </RouteErrorBoundary>
          } />
          
          <Route path="/map" element={
            <RouteErrorBoundary routeName="Map">
              <MapScreen />
            </RouteErrorBoundary>
          } />
          
          <Route path="/tickets" element={
            <RouteErrorBoundary routeName="Tickets">
              <Tickets />
            </RouteErrorBoundary>
          } />
          
          <Route path="/services" element={
            <RouteErrorBoundary routeName="Services">
              <Services />
            </RouteErrorBoundary>
          } />
          
          <Route path="/exhibitor-login" element={
            <RouteErrorBoundary routeName="Login">
              <Login />
            </RouteErrorBoundary>
          } />
          
          <Route path="/visitor-registration" element={
            <RouteErrorBoundary routeName="Registration">
              <VisitorRegistration />
            </RouteErrorBoundary>
          } />
          
          <Route path="/visitor-profile" element={
            <RouteErrorBoundary routeName="Profile">
              <VisitorProfile />
            </RouteErrorBoundary>
          } />

          <Route path="/info" element={
            <RouteErrorBoundary routeName="Info">
              <Info />
            </RouteErrorBoundary>
          } />
          
          <Route path="/events" element={
            <RouteErrorBoundary routeName="Events">
              <Events />
            </RouteErrorBoundary>
          } />
           <Route path="/sponsors" element={
            <RouteErrorBoundary routeName="Events">
              <SponsorsScreen />
            </RouteErrorBoundary>
          } />

          <Route path="/favorites" element={
            <RouteErrorBoundary routeName="My Visit">
              <Favorites />
            </RouteErrorBoundary>
          } />
        </Routes>
        </Suspense>
        </motion.div>
        </AnimatePresence>
        </MotionConfig>

        {shouldShowBottomNavbar && <BottomNavBar />}
         {/* {process.env.NODE_ENV === 'development' && <NotificationTester />}  */}
        <AppToasts />
        <IOSInstallModal />
        <AndroidInstallModal
          deferredPrompt={deferredPrompt}
          onPromptResult={handleInstallPromptResult}
        />
      </div>
    </>
  );
}

function App() {
  return (
  <ErrorBoundary>
    <DeviceGuard>
      <LanguageProvider>
        <ExhibitionProvider>
          <Router>
            <AppContent />
          </Router>
        </ExhibitionProvider>
      </LanguageProvider>
    </DeviceGuard>
  </ErrorBoundary>
  );
}

export default App;